import {
  Button,
  InlineNotification,
  MenuButton,
  MenuItem,
  OverflowMenu,
  SkeletonText,
} from '@carbon/react';
import {
  SidePanelClose,
  SidePanelOpen,
  Information,
} from '@carbon/icons-react';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { Well as WellIcon } from 'components/icons/well';
import { PageHeader } from 'components/page-header';
import {
  useCreateWorkspace,
  useWellWorkspaces,
} from 'pages/well-workspaces/hooks';
import { NameWorkspaceModal } from 'pages/well-workspaces/name-workspace-modal';
import { useAtomValue } from 'jotai';
import { useHistory, useLocation } from 'react-router-dom';
import { PlainLink } from 'components/link';
import { WellPageCards } from './well-page-cards';
import { useWellboreGeometryWorkspaces } from './geometry-workspaces-drawer';
import { formatNumber } from 'utils/number';
import { WellsResponse, WellVersionsResponse } from 'types/api-responses';
import { useWellAtom } from './atoms';
import { CaretDown, Checkmark } from '@carbon/react/icons';
import { activePreDrillWorkspaceHrefAtom } from 'components/pre-drill-workspace-launcher';
import {
  wellWorkspacePath,
  type WellWorkspaceLocationState,
} from 'pages/well-workspaces/utils';
import { PreDrillWorkspaceShell } from 'pages/pre-drill-wellbore-workspace/pre-drill-workspace-shell';

import style from './well-page.module.scss';
import { useState, type ReactNode } from 'react';
import { useWellVersionsAtom } from 'components/well-page-content/atoms';
import classNames from 'classnames';
import { parseUTCdateTime } from 'utils/dates';

const WellVersionSwitcher = ({
  well,
  onVersionChange,
}: {
  well: WellsResponse;
  onVersionChange: (version: string) => void;
}) => {
  const wellVersionAtom = useWellVersionsAtom(
    `${well.resources.versions.href}`
  );
  // Getting well versions and displaying in dropdown
  const wellVersions = useAtomValue(wellVersionAtom);

  if (wellVersions.loading || wellVersions.data === undefined) {
    return (
      <SkeletonText className={style.versionSwitcherSkeleton} width={'30px'} />
    );
  }

  if ('error' in wellVersions.data) {
    return (
      <InlineNotification
        kind="error"
        title={'Error loading version details for this well.'}
        lowContrast
      />
    );
  }

  const selectedVersion = wellVersions.data.find(
    (version) => version.versionId === well.versionId
  );

  const versionLabelRenderer = (
    version: WellVersionsResponse,
    isTextHighlight = false
  ) => {
    // Formatting the date to display in the dropdown list
    const formattedDisplayTime =
      version.versionId === 'current'
        ? parseUTCdateTime(version.beginTime, true)
        : `${parseUTCdateTime(version.beginTime, true)}${
            version.endTime
              ? ` - ${parseUTCdateTime(version.endTime, true)}`
              : ''
          }`;

    return (
      <span
        className={classNames({
          [style.selectedVersionLabel]: isTextHighlight,
        })}
      >
        {version.versionName}
        {version.versionId === 'current' && ' Current'}&nbsp;
        <span>({formattedDisplayTime})</span>
      </span>
    );
  };

  return (
    <div className={style.versionSwitcherContainer}>
      {selectedVersion ? (
        <span>{versionLabelRenderer(selectedVersion, true)}</span>
      ) : null}
      <OverflowMenu
        renderIcon={CaretDown}
        className={style.versionSwitcherMenu}
        size="sm"
        data-testid="wells-version-switcher"
      >
        <>
          <ul className={style.versionSwitcherList}>
            {wellVersions.data.map((version) => {
              // This calculation is for initial check mark display
              const isSelectedVersion = well.versionId === version.versionId;
              return (
                <li
                  key={version.versionName}
                  onClick={() => {
                    onVersionChange(version.versionId);
                  }}
                >
                  <Checkmark
                    visibility={isSelectedVersion ? 'visible' : 'hidden'}
                  />
                  {versionLabelRenderer(version, isSelectedVersion)}
                </li>
              );
            })}
          </ul>
        </>
      </OverflowMenu>
    </div>
  );
};

/**
 * A View-menu label with the count of workspaces behind it, once that count is known.
 * Without the count until then rather than "(0)", which would claim there are none.
 * @param {string} label The workspace type.
 * @param {number} [count] How many this well is in, when loaded.
 * @return {string} The label.
 */
const withCount = (label: string, count?: number) => {
  if (count === undefined) return label;
  return `${label} (${formatNumber({
    value: count,
    minPrecision: 0,
    includeCommas: true,
  })})`;
};

const Header = ({
  well,
  onVersionChange,
  onAddWorkspace,
  addingWorkspace,
  railCollapsed,
  onToggleRail,
  onViewWorkspaces,
  onViewGeometryWorkspaces,
  onOpenViewMenu,
  workspaceCounts,
}: {
  well: WellsResponse | undefined;
  onVersionChange: (version: string) => void;
  onAddWorkspace: () => void;
  addingWorkspace: boolean;
  railCollapsed: boolean;
  onToggleRail: () => void;
  onViewWorkspaces: () => void;
  onViewGeometryWorkspaces: () => void;
  /** The View menu was opened; the page starts the counts it had been holding. */
  onOpenViewMenu: () => void;
  workspaceCounts: { well?: number; geometry?: number };
}) => {
  return (
    <PageHeader
      // Mock v13 heads this page with one compact line — the well name beside its
      // icon — and carries no breadcrumb row above it.
      compact
      breadcrumbs={[]}
      // The well's name, as mock v13 heads the page — not "UWI <number>". The UWI is
      // still the first field on the Well Details card, so nothing is lost, and the
      // name is what a reader recognises the page by. Falls back to the UWI for a well
      // with no lease name.
      title={
        well ? (
          well.name || `UWI ${well.uwi}`
        ) : (
          <SkeletonText heading width="200px" />
        )
      }
      titleDetail={
        // The version lives in the settings rail now (mock v13 puts it at the top of
        // Layout settings), so the header does not repeat it. It comes back when the
        // rail is hidden, which would otherwise leave no way to change version at all.
        well && railCollapsed ? (
          <WellVersionSwitcher well={well} onVersionChange={onVersionChange} />
        ) : null
      }
      iconComponent={<WellIcon medium hasBackground={false} />}
      actions={
        <>
          <Button
            kind="tertiary"
            // sm across the row, as the mock has it — md would make the two icon
            // buttons taller than the View and Action beside them.
            size="sm"
            hasIconOnly
            renderIcon={railCollapsed ? SidePanelOpen : SidePanelClose}
            iconDescription={
              railCollapsed ? 'Show settings panel' : 'Hide settings panel'
            }
            tooltipPosition="bottom"
            onClick={onToggleRail}
          />
          <EventsPanelOpenButton buttonSize="sm" />
          {well ? (
            <>
              {/* "View" lists the workspace types this well belongs to, each with how
                  many it is in, and opens the drawer scoped to one. Two of the mock's
                  types exist today — Well and Wellbore Geometry workspaces; DCA, Permit
                  Geometry and Evaluations have no per-well endpoint yet. The geometry
                  count is fetched the first time the menu opens rather than with the
                  page, so a reader who never opens it never pays for it. Captured on
                  the wrapper because MenuButton has no open callback of its own. */}
              <span onClickCapture={onOpenViewMenu}>
                <MenuButton label="View" kind="tertiary" size="sm">
                  <MenuItem
                    label={withCount('Well Workspaces', workspaceCounts.well)}
                    onClick={onViewWorkspaces}
                  />
                  <MenuItem
                    label={withCount(
                      'Wellbore Geometry Workspaces',
                      workspaceCounts.geometry
                    )}
                    onClick={onViewGeometryWorkspaces}
                  />
                </MenuButton>
              </span>
              {/* A workspace is where this well's data can be edited and verified; the
                  Well page itself stays read-only, so this is how a reader gets from
                  one to the other without going back to the listing to build one by
                  hand. A menu, as the mock has it, because Epic 2 adds the permit- and
                  wellbore-geometry workspaces beside it. */}
              <MenuButton label="Action" size="sm">
                {/* A reconciled (merged) well is a read-only view, so it holds no
                    wells of its own to seed a workspace — creating one would fail
                    the "at least one well" check with an opaque error. Disable the
                    action; the page's deduplicated banner already states the reason
                    and links to the surviving well. */}
                <MenuItem
                  label={
                    addingWorkspace ? 'Creating…' : 'Create Well Workspace'
                  }
                  disabled={addingWorkspace || Boolean(well.mergedInto)}
                  onClick={onAddWorkspace}
                />
              </MenuButton>
            </>
          ) : null}
        </>
      }
    ></PageHeader>
  );
};

const EventsContent = ({ well }: { well: WellsResponse }) => {
  return (
    <EventsPanel
      header={well.name}
      title="Well"
      helperText="Context allows you to share information about a well."
      eventsHref={well.resources.events.href}
      commentsHref={well.resources.comments.href}
    />
  );
};

/**
 * A compact, low-contrast info callout styled like Carbon's info notification (blue left-accent,
 * light-blue ground, 16px info glyph) but hand-rolled so links live INSIDE the box — Carbon's
 * InlineNotification rejects interactive children. Each banner renders its own single-line body
 * (bold lead + detail on one line) so the callout stays ~one line tall per Eira's UX note.
 * @param {object} props The body content to render inside the box.
 * @return {JSX.Element} The callout.
 */
function DedupInfoCallout({ children }: { children: ReactNode }) {
  return (
    // role="status" restores the live-region announcement Carbon's InlineNotification gave: the banner
    // mounts after the well fetch resolves, so assistive tech should announce the read-only state.
    <div className={style.dedupCallout} role="status">
      <Information size={16} className={style.dedupCalloutIcon} />
      <div className={style.dedupCalloutBody}>{children}</div>
    </div>
  );
}

/**
 * Read-only banner shown when the viewed well is a reconciled duplicate — it was merged into a
 * survivor, so this page is a read-only view (edits are refused by WellsController#update). The link to
 * the surviving well sits inside the box.
 * @param {object} props The well payload carrying `mergedInto`.
 * @return {JSX.Element} The banner.
 */
function DeduplicatedWellBanner({ well }: { well: WellsResponse }) {
  const mergedInto = well.mergedInto;
  // The page renders this only when mergedInto is set; the guard narrows the type and never fires.
  if (!mergedInto) return null;
  return (
    <DedupInfoCallout>
      <p className={style.dedupCalloutText}>
        <strong>This well was deduplicated.</strong> This is a read-only view of
        a well that was merged into{' '}
        {mergedInto.href ? (
          <PlainLink href={mergedInto.href}>{mergedInto.name}</PlainLink>
        ) : (
          mergedInto.name
        )}{' '}
        (UWI {mergedInto.uwi}).
      </p>
    </DedupInfoCallout>
  );
}

/**
 * One reconciled duplicate's name linked to its (read-only) page, with its UWI — the shared bit of
 * both the single-well inline sentence and the multi-well list, so the markup stays in one place.
 * @param {object} props The reconciled duplicate to render.
 * @return {JSX.Element} The linked name and UWI.
 */
function ReconciledDuplicateLink({
  duplicate,
}: {
  duplicate: NonNullable<WellsResponse['reconciledWells']>[number];
}) {
  return (
    <>
      <PlainLink href={duplicate.href}>{duplicate.name}</PlainLink> (UWI{' '}
      {duplicate.uwi})
    </>
  );
}

/**
 * Survivor-side banner — the reverse of DeduplicatedWellBanner. When this well absorbed reconciled
 * duplicates (it is the survivor of one or more merges), their data was moved onto it and the duplicates
 * turned read-only; this lists them inside the box so a reader can see what was merged in and open each
 * one. Rendered only when the well has at least one reconciled duplicate.
 * @param {object} props The well payload carrying `reconciledWells`.
 * @return {JSX.Element} The banner.
 */
function ReconciledWellsBanner({ well }: { well: WellsResponse }) {
  const reconciled = well.reconciledWells ?? [];
  const count = reconciled.length;
  // A single merged well reads inline on one line, mirroring the duplicate banner; multiple need a list.
  if (count === 1) {
    return (
      <DedupInfoCallout>
        <p className={style.dedupCalloutText}>
          <strong>This well is the survivor of 1 merged well.</strong>{' '}
          <ReconciledDuplicateLink duplicate={reconciled[0]} /> was deduplicated
          into this one and is now read-only.
        </p>
      </DedupInfoCallout>
    );
  }
  return (
    <DedupInfoCallout>
      <p className={style.dedupCalloutText}>
        <strong>This well is the survivor of {count} merged wells.</strong> The
        wells below were deduplicated into this one and are now read-only:
      </p>
      <ul className={style.reconciledWellsList}>
        {reconciled.map((duplicate) => (
          <li key={duplicate.uwi}>
            <ReconciledDuplicateLink duplicate={duplicate} />
          </li>
        ))}
      </ul>
    </DedupInfoCallout>
  );
}

const ShowWellPage = () => {
  const { pathname } = useLocation();
  // Maintaining the wells version href and loading the page with the selected href
  const [versionId, setVersionId] = useState<string | undefined>(undefined);
  const wellAtom = useWellAtom(pathname, versionId);
  const well = useAtomValue(wellAtom);
  // When a pre-drill workspace is opened it takes over the whole page (Type
  // Curve convention); Back clears the atom and restores the well page.
  const activePreDrillHref = useAtomValue(activePreDrillWorkspaceHrefAtom);

  const wellUwi = well.data && !('error' in well.data) ? well.data.uwi : null;

  // "Add Well Workspace" (Story 3). The header's menu item opens a small modal for the
  // workspace's name — pre-filled with the backend's default — and the create runs from
  // there, with a refusal shown in the modal. On success the new workspace opens at its
  // own URL, landing on this well.
  const history = useHistory<WellWorkspaceLocationState>();
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const workspace = useCreateWorkspace((created) => {
    setNameModalOpen(false);
    history.push(wellWorkspacePath(created.id), {
      well: wellUwi,
      created: true,
    });
  });

  // The settings rail and the Workspaces drawer are opened from the header but live in
  // the bounded shell below it, so their state sits here where both can reach it.
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [workspacesOpen, setWorkspacesOpen] = useState(false);
  const [geometryWorkspacesOpen, setGeometryWorkspacesOpen] = useState(false);
  // Set the first time the header's View menu opens; until then the geometry count
  // below is held, so the page does not fetch a list nobody has asked to see.
  const [viewMenuOpened, setViewMenuOpened] = useState(false);

  // The View menu's first count and the Workspaces drawer read this.
  const wellWorkspaces = useWellWorkspaces(wellUwi ?? undefined);
  // The View menu's second count. Held until the menu has been opened once; the drawer
  // reads the same query, so opening it after the menu costs nothing more.
  const geometryWorkspaces = useWellboreGeometryWorkspaces(
    viewMenuOpened ? (wellUwi ?? undefined) : undefined
  );

  if (well.data && 'error' in well.data)
    return (
      <InlineNotification kind="error" title={well.data.error} lowContrast />
    );

  if (activePreDrillHref) {
    return <PreDrillWorkspaceShell />;
  }

  return (
    <>
      {well.data?.mergedInto && <DeduplicatedWellBanner well={well.data} />}
      {well.data?.reconciledWells?.length ? (
        <ReconciledWellsBanner well={well.data} />
      ) : null}
      <Header
        well={well.data}
        onVersionChange={setVersionId}
        addingWorkspace={workspace.saving}
        onAddWorkspace={() => setNameModalOpen(true)}
        railCollapsed={railCollapsed}
        onToggleRail={() => setRailCollapsed((collapsed) => !collapsed)}
        // One drawer at a time: the two share the right edge of the shell.
        onViewWorkspaces={() => {
          setGeometryWorkspacesOpen(false);
          setWorkspacesOpen(true);
        }}
        onViewGeometryWorkspaces={() => {
          setWorkspacesOpen(false);
          setGeometryWorkspacesOpen(true);
        }}
        onOpenViewMenu={() => setViewMenuOpened(true)}
        workspaceCounts={{
          well: wellWorkspaces.data?.workspaces.length,
          geometry: geometryWorkspaces.data?.length,
        }}
      />
      <NameWorkspaceModal
        open={nameModalOpen}
        wellCount={1}
        saving={workspace.saving}
        error={workspace.error}
        onClose={() => {
          setNameModalOpen(false);
          workspace.reset();
        }}
        onSubmit={(name) => {
          if (wellUwi) workspace.create({ wellUwis: [wellUwi], name });
        }}
      />
      {/* The Well Workspace's layout, reduced to this one well (INS-10338 Story 3):
          the settings rail and a single column of section cards, with the workspace's
          tree view and dark header absent. The sections are the same components with
          the same props the grid layout rendered — only the framing changed. */}
      <WellPageCards
        well={well.data}
        uwi={well.data?.uwi || ''}
        versionId={versionId}
        railCollapsed={railCollapsed}
        workspacesOpen={workspacesOpen}
        onCloseWorkspaces={() => setWorkspacesOpen(false)}
        geometryWorkspacesOpen={geometryWorkspacesOpen}
        onCloseGeometryWorkspaces={() => setGeometryWorkspacesOpen(false)}
        onVersionChange={setVersionId}
      />
      {well.data ? <EventsContent well={well.data} /> : null}
    </>
  );
};

export { ShowWellPage };
